import { collection, getDocs, getDoc, doc, query, where, writeBatch, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ROLES } from '../hooks/useAuth';

// Firestore Timestamp'larını ve Date objelerini string'e çeviren yardımcı fonksiyon
const convertTimestampsToStrings = (obj) => {
  if (obj === null || obj === undefined) return obj;

  // Firestore Timestamp
  if (typeof obj.toDate === 'function') {
    return obj.toDate().toISOString();
  }

  // Normal Date objesi
  if (obj instanceof Date) {
    return obj.toISOString();
  }

  // Array'leri gez
  if (Array.isArray(obj)) {
    return obj.map(item => convertTimestampsToStrings(item));
  }

  // Objeleri gez
  if (typeof obj === 'object') {
    // Firestore'dan gelen doğrudan serialize edilmiş Timestamp nesnesi
    if ('seconds' in obj && 'nanoseconds' in obj && Object.keys(obj).length === 2) {
      return new Date(obj.seconds * 1000).toISOString();
    }

    const result = {};
    for (const key in obj) {
      result[key] = convertTimestampsToStrings(obj[key]);
    }
    return result;
  }

  // String, number, boolean döner
  return obj;
};

// ISO 8601 string kalıbını kontrol eden yardımcı fonksiyon
const isIsoDateString = (value) => {
  if (typeof value !== 'string') return false;
  // Sıkı bir ISO 8601 kontrolü
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;
  return isoRegex.test(value);
};

// String tarihlerini Date objelerine çeviren yardımcı fonksiyon
const convertStringsToDates = (obj) => {
  if (obj === null || obj === undefined) return obj;

  // Sadece ISO formatındaki tarih stringlerini Date yap
  if (isIsoDateString(obj)) {
    const date = new Date(obj);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertStringsToDates(item));
  }

  if (typeof obj === 'object') {
    const result = {};
    for (const key in obj) {
      result[key] = convertStringsToDates(obj[key]);
    }
    return result;
  }

  return obj;
};

// Batch write yardımcı fonksiyonu (Firestore 500 limit'ini aşmamak için)
// Mevcut tüm belgeleri ID'ye göre siler ve yeniden yazar
const batchWrite = async (db, items, collectionName) => {
  const BATCH_SIZE = 499; // Güvenli limit
  let totalWritten = 0;

  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const chunk = items.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    for (const item of chunk) {
      const itemDataRaw = { ...item };
      const itemId = itemDataRaw.id;
      delete itemDataRaw.id;
      const itemData = convertStringsToDates(itemDataRaw);
      const docRef = doc(db, collectionName, itemId);
      batch.set(docRef, itemData);
    }

    await batch.commit();
    totalWritten += chunk.length;
    console.log(`${collectionName}: ${totalWritten}/${items.length} yazıldı`);
  }

  return totalWritten;
};

// Sadece yeni (mevcut olmayan) belgeleri yazan batch write fonksiyonu
// Aynı ID'ye sahip belgeler zaten varsa atlanır — bu şekilde duplikasyon engellenir
const batchWriteOnlyNew = async (db, items, collectionName) => {
  if (items.length === 0) return { written: 0, skipped: 0 };

  // Koleksiyondaki mevcut tüm ID'leri çek
  const existingSnapshot = await getDocs(collection(db, collectionName));
  const existingIds = new Set(existingSnapshot.docs.map(d => d.id));

  // Sadece veritabanında henüz bulunmayanları filtrele
  const newItems = items.filter(item => !existingIds.has(item.id));
  const skippedCount = items.length - newItems.length;

  if (newItems.length === 0) {
    console.log(`${collectionName}: Tüm ${items.length} kayıt zaten mevcut, atlandı`);
    return { written: 0, skipped: skippedCount };
  }

  const written = await batchWrite(db, newItems, collectionName);
  console.log(`${collectionName}: ${written} yeni kayıt eklendi, ${skippedCount} mevcut kayıt atlandı`);
  return { written, skipped: skippedCount };
};

// Tüm verileri export et
export const exportData = async (userRole = null, userId = null) => {
  try {
    console.log('Backup başlatılıyor...', { userRole, userId });

    // 1. Müşterileri çek - Yazılımcı rolü için sadece kendi oluşturduğu müşteriler
    let customersQuery = collection(db, 'customers');
    if (userRole === ROLES.YAZILIMCI && userId) {
      customersQuery = query(collection(db, 'customers'), where('createdBy', '==', userId));
    }

    const customersSnapshot = await getDocs(customersQuery);
    const customers = customersSnapshot.docs.map(doc => {
      const data = convertTimestampsToStrings(doc.data());
      return { id: doc.id, ...data };
    });

    // Mevcut müşteri ID setini oluştur — orphaned siparişleri filtrelemek için
    const customerIdSet = new Set(customers.map(c => c.id));

    // 2. Siparişleri çek — sadece mevcut müşterilere ait siparişler dahil edilir
    // (Silinen müşterilere ait orphaned siparişler yedek sayısını şişirdiği için hariç tutulur)
    let orders = [];
    if (userRole === ROLES.YAZILIMCI && userId && customers.length > 0) {
      const customerIds = customers.map(c => c.id);
      if (customerIds.length > 0) {
        const allOrders = [];
        // Firestore 'in' query limit is 10 items (newer versions 30, but 10 is safe)
        for (let i = 0; i < customerIds.length; i += 10) {
          const batch = customerIds.slice(i, i + 10);
          const batchQuery = query(collection(db, 'orders'), where('customerId', 'in', batch));
          const batchSnapshot = await getDocs(batchQuery);
          const batchOrders = batchSnapshot.docs.map(doc => {
            const data = convertTimestampsToStrings(doc.data());
            return { id: doc.id, ...data };
          });
          allOrders.push(...batchOrders);
        }
        orders = allOrders;
      }
    } else {
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      orders = ordersSnapshot.docs
        .map(doc => {
          const data = convertTimestampsToStrings(doc.data());
          return { id: doc.id, ...data };
        })
        .filter(order => customerIdSet.has(order.customerId));
    }

    // 3. Firmaları (Companies) çek - Bunlar siparişlerle alakalı olduğu için yedeklenmeli
    const companiesSnapshot = await getDocs(collection(db, 'companies'));
    const companies = companiesSnapshot.docs.map(docSnap => {
      const data = convertTimestampsToStrings(docSnap.data());
      return { id: docSnap.id, ...data };
    });

    // 4. Ürün listesini çek (productItems)
    const productItemsSnapshot = await getDocs(collection(db, 'productItems'));
    const productItems = productItemsSnapshot.docs.map(docSnap => {
      const data = convertTimestampsToStrings(docSnap.data());
      return { id: docSnap.id, ...data };
    });

    // 5. Global ayarları çek (settings/global — motor fiyatları, kur ayarları, vb.)
    const settingsDocRef = doc(db, 'settings', 'global');
    const settingsSnap = await getDoc(settingsDocRef);
    const settings = settingsSnap.exists()
      ? { id: 'global', ...convertTimestampsToStrings(settingsSnap.data()) }
      : null;

    // Toplam parça sayısını (adet) hesapla (Dashboard'daki mantıkla aynı)
    const totalPieces = orders.reduce((sum, order) => {
      if (order.productType === 'plise') {
        return sum + (order.pliseItems || []).reduce((pSum, item) => pSum + (parseInt(item.quantity) || 0), 0);
      }
      return sum + (parseInt(order.quantity) || 0);
    }, 0);

    // Backup objesi oluştur
    const backup = {
      exportDate: new Date().toISOString(),
      version: '3.0',
      totalCustomers: customers.length,
      totalOrders: orders.length,
      totalPieces: totalPieces,
      totalCompanies: companies.length,
      totalProductItems: productItems.length,
      hasSettings: settings !== null,
      customers: customers,
      orders: orders,
      companies: companies,
      productItems: productItems,
      settings: settings
    };

    // JSON olarak indir
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Kulis-Perde-Full-Backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`Backup tamamlandı: ${customers.length} müşteri, ${orders.length} sipariş (${totalPieces} adet), ${companies.length} firma, ${productItems.length} ürün`);
    return {
      success: true,
      customers: customers.length,
      orders: orders.length,
      pieces: totalPieces,
      companies: companies.length,
      productItems: productItems.length,
      hasSettings: settings !== null
    };
  } catch (error) {
    console.error('Backup hatası:', error);
    throw new Error('Backup oluşturulurken hata oluştu: ' + error.message);
  }
};

// JSON dosyasından verileri import et
export const importData = async (file) => {
  try {
    console.log('Import başlatılıyor...');

    // Dosyayı oku
    const text = await file.text();
    const backup = JSON.parse(text);

    // Backup formatını kontrol et
    if (!backup.customers || !backup.orders) {
      throw new Error('Geçersiz backup dosyası formatı');
    }

    // 1. Müşterileri batch olarak import et (ID bazlı overwrite — temel yedek verisi)
    console.log(`${backup.customers.length} müşteri import ediliyor...`);
    const importedCustomers = await batchWrite(db, backup.customers, 'customers');

    // 2. Siparişleri batch olarak import et (ID bazlı overwrite)
    console.log(`${backup.orders.length} sipariş import ediliyor...`);
    const importedOrders = await batchWrite(db, backup.orders, 'orders');

    // 3. Firmaları (Companies) import et — sadece yeni olanlar eklenir
    let importedCompanies = 0;
    if (backup.companies && backup.companies.length > 0) {
      console.log(`${backup.companies.length} firma kontrol ediliyor...`);
      const companiesResult = await batchWriteOnlyNew(db, backup.companies, 'companies');
      importedCompanies = companiesResult.written;
    }

    // 4. Ürün listesini import et — sadece yeni (mevcut olmayan ID) olanlar eklenir
    let productItemsResult = { written: 0, skipped: 0 };
    if (backup.productItems && backup.productItems.length > 0) {
      console.log(`${backup.productItems.length} ürün kontrol ediliyor...`);
      productItemsResult = await batchWriteOnlyNew(db, backup.productItems, 'productItems');
    }

    // 5. Settings'i import et
    // settings/global yoksa tüm ayarları yaz; varsa sadece eksik alanları merge et
    let settingsStatus = 'not_in_backup';
    if (backup.settings) {
      const settingsDocRef = doc(db, 'settings', 'global');
      const existingSettings = await getDoc(settingsDocRef);

      const settingsDataRaw = { ...backup.settings };
      delete settingsDataRaw.id;
      const settingsData = convertStringsToDates(settingsDataRaw);

      if (!existingSettings.exists()) {
        // Hiç settings yoksa tüm yedek verisini yaz
        await setDoc(settingsDocRef, settingsData);
        settingsStatus = 'created';
        console.log('settings/global: yedekten oluşturuldu');
      } else {
        // Settings zaten varsa sadece eksik alanları ekle (mevcut değerler korunur)
        const existingData = existingSettings.data();
        const missingFields = {};
        for (const key in settingsData) {
          if (!(key in existingData)) {
            missingFields[key] = settingsData[key];
          }
        }
        if (Object.keys(missingFields).length > 0) {
          await setDoc(settingsDocRef, missingFields, { merge: true });
          settingsStatus = 'merged';
          console.log(`settings/global: ${Object.keys(missingFields).length} eksik alan eklendi`);
        } else {
          settingsStatus = 'skipped';
          console.log('settings/global: mevcut ayarlar güncel, atlandı');
        }
      }
    }

    const result = {
      success: true,
      addedCustomers: importedCustomers,
      updatedCustomers: 0,
      addedOrders: importedOrders,
      updatedOrders: 0,
      addedCompanies: importedCompanies,
      totalCustomers: backup.totalCustomers,
      totalOrders: backup.totalOrders,
      totalCompanies: backup.totalCompanies || 0,
      addedProductItems: productItemsResult.written,
      skippedProductItems: productItemsResult.skipped,
      totalProductItems: backup.totalProductItems || backup.productItems?.length || 0,
      settingsStatus
    };

    console.log('Import tamamlandı:', result);
    return result;
  } catch (error) {
    console.error('Import hatası:', error);
    throw new Error('Import işlemi sırasında hata oluştu: ' + error.message);
  }
};

// Dosya formatını kontrol et
export const validateBackupFile = (file) => {
  // Dosya tipi kontrolü
  if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
    throw new Error('Sadece JSON dosyaları desteklenir');
  }

  // Dosya boyutu kontrolü (max 50MB)
  const maxSize = 50 * 1024 * 1024; // 50MB
  if (file.size > maxSize) {
    throw new Error('Dosya boyutu çok büyük (maksimum 50MB)');
  }

  return true;
};
